import { getDb } from '@/db/schema';
import { ParseError, SourceError } from '@/lib/errors';
import { getProxyBase } from '@/lib/proxy';

const DIAGNOSTICS_META_KEY = 'diagnostics.events';
const MAX_EVENTS = 100;
const EVENT_TTL_MS = 24 * 60 * 60 * 1000;

export type DiagnosticArea = 'reader' | 'source' | 'image' | 'ui';
export type DiagnosticLevel = 'info' | 'warn' | 'error';

export interface DiagnosticEvent {
  t: number;
  area: DiagnosticArea;
  name: string;
  level?: DiagnosticLevel;
  data?: Record<string, unknown>;
}

export interface DebugReport {
  generatedAt: string;
  app: {
    url: string;
    userAgent: string;
    viewport: { width: number; height: number; devicePixelRatio: number };
    proxyBase?: string;
  };
  context?: {
    sourceId?: string;
    comicId?: string;
    page?: number;
    totalPages?: number;
    readerMode?: string;
  };
  events: DiagnosticEvent[];
  stores: {
    prefs: Array<{ key: string; value: unknown }>;
    history: unknown[];
    ehentaiPtokenMeta: Array<{
      key: string;
      entries: number;
      hasTotal: boolean;
      total?: number;
      firstPage?: number;
      lastPage?: number;
      cachedAt?: number;
    }>;
  };
}

type DiagnosticMetaValue = {
  events?: DiagnosticEvent[];
};

interface ClipboardFallbackTarget {
  textArea: HTMLTextAreaElement;
  cleanup: () => void;
}

export interface ClipboardWriteOptions {
  target?: HTMLTextAreaElement | null;
}

export class DebugReportCopyBlockedError extends Error {
  readonly reportText: string;

  constructor(reportText: string, cause: unknown) {
    super('Clipboard access was blocked. The debug report is available for manual copy.', { cause });
    this.name = 'DebugReportCopyBlockedError';
    this.reportText = reportText;
  }
}

let memoryEvents: DiagnosticEvent[] = [];

export function recordDiagnostic(
  area: DiagnosticArea,
  name: string,
  data?: Record<string, unknown>,
  level: DiagnosticLevel = 'info',
): void {
  if (typeof window === 'undefined') return;
  const event: DiagnosticEvent = {
    t: Date.now(),
    area,
    name,
    level,
  };
  const safeData = sanitizeRecord(data);
  if (safeData !== undefined) event.data = safeData;

  rememberEvent(event);
  void appendDiagnosticEvent(event);
}

export function summarizeDiagnosticError(err: unknown): Record<string, unknown> {
  return summarizeErrorValue(err);
}

function summarizeErrorValue(err: unknown, depth = 0): Record<string, unknown> {
  if (err instanceof Error) {
    const summary: Record<string, unknown> = {
      name: err.name,
      message: err.message,
    };
    if (err instanceof SourceError && err.source) summary.source = err.source;
    if (err instanceof ParseError && err.snippet) summary.snippet = err.snippet;
    const cause = (err as { cause?: unknown }).cause;
    if (cause instanceof Error) {
      summary.cause = depth >= 2 ? { name: cause.name, message: cause.message } : summarizeErrorValue(cause, depth + 1);
    } else if (cause !== undefined) {
      summary.cause = String(cause);
    }
    return summary;
  }
  return { message: String(err) };
}

export async function buildDebugReport(context?: DebugReport['context']): Promise<DebugReport> {
  const db = await getDb();
  const [events, prefs, historyRows, metaRows, proxyBase] = await Promise.all([
    readDiagnosticEvents(),
    db.getAll('prefs'),
    db.getAll('history'),
    db.getAll('meta'),
    getProxyBase().catch(() => undefined),
  ]);

  const sourceId = context?.sourceId;
  const comicId = context?.comicId;
  const filteredHistory = historyRows.filter((row) => {
    if (typeof row !== 'object' || row === null) return false;
    const candidate = row as { source?: unknown; comicId?: unknown };
    if (sourceId && candidate.source !== sourceId) return false;
    if (comicId && candidate.comicId !== comicId) return false;
    return sourceId !== undefined || comicId !== undefined ? true : historyRows.indexOf(row) < 20;
  });

  const ehentaiPtokenMeta = metaRows
    .filter((row) => typeof row.key === 'string' && row.key.startsWith('eh.ptokens:'))
    .filter((row) => (comicId ? row.key.includes(comicId.split(':')[0] ?? comicId) : true))
    .map((row) => summarizePtokenMeta(row.key, row.value))
    .filter((row): row is NonNullable<ReturnType<typeof summarizePtokenMeta>> => row !== undefined);

  return {
    generatedAt: new Date().toISOString(),
    app: {
      url: typeof location !== 'undefined' ? location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport: {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
        devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
      },
      ...(proxyBase !== undefined ? { proxyBase } : {}),
    },
    ...(context !== undefined ? { context } : {}),
    events,
    stores: {
      prefs: prefs.map((row) => ({ key: row.key, value: sanitizeValue(row.value) })),
      history: filteredHistory.map((row) => sanitizeValue(row)),
      ehentaiPtokenMeta,
    },
  };
}

export async function copyDebugReport(context?: DebugReport['context']): Promise<void> {
  const fallbackTarget = prepareClipboardFallbackTarget();
  try {
    const report = await buildDebugReport(context);
    const text = JSON.stringify(report, null, 2);
    try {
      await copyTextToClipboard(text, { target: fallbackTarget?.textArea });
    } catch (err) {
      throw new DebugReportCopyBlockedError(text, err);
    }
  } finally {
    fallbackTarget?.cleanup();
  }
}

export function getManualDebugReportText(err: unknown): string | undefined {
  return err instanceof DebugReportCopyBlockedError ? err.reportText : undefined;
}

export async function copyTextToClipboard(
  text: string,
  options: ClipboardWriteOptions = {},
): Promise<void> {
  let clipboardFailure: unknown;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (err) {
      clipboardFailure = err;
    }
  }

  if (copyTextWithSelectionFallback(text, options.target ?? undefined)) return;

  if (clipboardFailure instanceof Error) {
    throw new Error(`Clipboard API failed: ${clipboardFailure.message}`);
  }
  throw new Error('Clipboard API is unavailable in this browser.');
}

function prepareClipboardFallbackTarget(): ClipboardFallbackTarget | undefined {
  if (typeof document === 'undefined' || !document.body) return undefined;
  const textArea = document.createElement('textarea');
  textArea.setAttribute('readonly', 'true');
  textArea.setAttribute('aria-hidden', 'true');
  Object.assign(textArea.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    fontSize: '16px',
  });

  const activeElement = document.activeElement;
  const selection = document.getSelection();
  const selectedRanges: Range[] = [];
  if (selection) {
    for (let index = 0; index < selection.rangeCount; index += 1) {
      selectedRanges.push(selection.getRangeAt(index).cloneRange());
    }
  }

  document.body.appendChild(textArea);
  textArea.focus({ preventScroll: true });
  textArea.select();

  return {
    textArea,
    cleanup: () => {
      textArea.remove();
      if (activeElement instanceof HTMLElement) activeElement.focus({ preventScroll: true });
      if (selection) {
        selection.removeAllRanges();
        for (const range of selectedRanges) selection.addRange(range);
      }
    },
  };
}

function copyTextWithSelectionFallback(text: string, target?: HTMLTextAreaElement): boolean {
  if (typeof document === 'undefined' || !document.body) return false;

  const prepared = target ? undefined : prepareClipboardFallbackTarget();
  const textArea = target ?? prepared?.textArea;
  if (!textArea) return false;

  textArea.value = text;
  textArea.focus({ preventScroll: true });
  textArea.select();
  textArea.setSelectionRange(0, textArea.value.length);

  let handledCopyEvent = false;
  const handleCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
    handledCopyEvent = true;
  };
  document.addEventListener('copy', handleCopy);

  try {
    return document.execCommand('copy') || handledCopyEvent;
  } catch {
    return false;
  } finally {
    document.removeEventListener('copy', handleCopy);
    prepared?.cleanup();
  }
}

async function appendDiagnosticEvent(event: DiagnosticEvent): Promise<void> {
  try {
    const db = await getDb();
    const row = await db.get('meta', DIAGNOSTICS_META_KEY);
    const current = readEventsFromMeta(row?.value);
    const cutoff = Date.now() - EVENT_TTL_MS;
    const events = [...current.filter((item) => item.t >= cutoff), event].slice(-MAX_EVENTS);
    await db.put('meta', { key: DIAGNOSTICS_META_KEY, value: { events } });
  } catch {
    // Diagnostics must never affect reader/source behavior.
  }
}

async function readDiagnosticEvents(): Promise<DiagnosticEvent[]> {
  try {
    const db = await getDb();
    const row = await db.get('meta', DIAGNOSTICS_META_KEY);
    const cutoff = Date.now() - EVENT_TTL_MS;
    return mergeEvents(readEventsFromMeta(row?.value).filter((event) => event.t >= cutoff));
  } catch {
    return mergeEvents([]);
  }
}

function readEventsFromMeta(value: unknown): DiagnosticEvent[] {
  if (typeof value !== 'object' || value === null) return [];
  const events = (value as DiagnosticMetaValue).events;
  if (!Array.isArray(events)) return [];
  return events.filter(isDiagnosticEvent);
}

function rememberEvent(event: DiagnosticEvent): void {
  const cutoff = Date.now() - EVENT_TTL_MS;
  memoryEvents = [...memoryEvents.filter((item) => item.t >= cutoff), event].slice(-MAX_EVENTS);
}

function mergeEvents(persisted: DiagnosticEvent[]): DiagnosticEvent[] {
  const cutoff = Date.now() - EVENT_TTL_MS;
  const byKey = new Map<string, DiagnosticEvent>();
  for (const event of [...persisted, ...memoryEvents]) {
    if (event.t < cutoff) continue;
    byKey.set(`${event.t}:${event.area}:${event.name}:${JSON.stringify(event.data ?? {})}`, event);
  }
  return Array.from(byKey.values())
    .sort((a, b) => a.t - b.t)
    .slice(-MAX_EVENTS);
}

function isDiagnosticEvent(value: unknown): value is DiagnosticEvent {
  if (typeof value !== 'object' || value === null) return false;
  const event = value as Partial<DiagnosticEvent>;
  return typeof event.t === 'number' && typeof event.area === 'string' && typeof event.name === 'string';
}

function summarizePtokenMeta(key: string, value: unknown):
  | {
      key: string;
      entries: number;
      hasTotal: boolean;
      total?: number;
      firstPage?: number;
      lastPage?: number;
      cachedAt?: number;
    }
  | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const row = value as {
    entries?: unknown;
    total?: unknown;
    cachedAt?: unknown;
  };
  const entries = Array.isArray(row.entries) ? row.entries : [];
  const pages = entries
    .map((entry) => (Array.isArray(entry) && typeof entry[0] === 'number' ? entry[0] : undefined))
    .filter((page): page is number => page !== undefined)
    .sort((a, b) => a - b);
  const summary: ReturnType<typeof summarizePtokenMeta> = {
    key,
    entries: entries.length,
    hasTotal: typeof row.total === 'number',
  };
  if (typeof row.total === 'number') summary.total = row.total;
  if (pages[0] !== undefined) summary.firstPage = pages[0];
  if (pages[pages.length - 1] !== undefined) summary.lastPage = pages[pages.length - 1];
  if (typeof row.cachedAt === 'number') summary.cachedAt = row.cachedAt;
  return summary;
}

function sanitizeRecord(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (data === undefined) return undefined;
  const sanitized = sanitizeValue(data);
  return typeof sanitized === 'object' && sanitized !== null && !Array.isArray(sanitized)
    ? (sanitized as Record<string, unknown>)
    : undefined;
}

function sanitizeValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      if (isSensitiveKey(key)) {
        out[key] = '[redacted]';
      } else if (key.toLowerCase().includes('url')) {
        out[key] = typeof child === 'string' ? summarizeUrl(child) : sanitizeValue(child);
      } else {
        out[key] = sanitizeValue(child);
      }
    }
    return out;
  }
  return String(value);
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower.includes('cookie') ||
    lower.includes('token') ||
    lower.includes('secret') ||
    lower.includes('password') ||
    lower.includes('ciphertext') ||
    lower === 'iv'
  );
}

function sanitizeString(value: string): string {
  if (value.length > 300) return `${value.slice(0, 300)}…`;
  return value;
}

function summarizeUrl(value: string): string {
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return sanitizeString(value);
  }
}
